import React, { FC } from 'react';
import { formatCurrency, floatValueHelpers } from '@factorin/util';
import { Field, useForm } from 'react-final-form';

import { Stack, InputField, Chip, TabOption, Typography, Tabs } from 'Modules/ui3';
import { validators } from 'Modules/core/forms';
import { WithT } from 'Modules/core/i18n';

import { PRICE_LIMITS, basePrices, tabsOptions } from './utils';
import { FormValues, Role } from '../types';
import { useListTranslate } from '../../../utils';


interface CommonBlockProps extends WithT {
    role: Role;
    price: FormValues['price'];
}

export const CommonBlock: FC<CommonBlockProps> = ({ t, price, role }) => {
    const { change, getFieldState } = useForm();
    return (
        <>
            <Typography variant='boldTitle5' color='grayscale.shade95'>
                {t('role.title')}
            </Typography>
            <Tabs
                options={useListTranslate<TabOption>(t, tabsOptions)}
                variant='button'
                value={role}
                onChange={value => change('role', value)}
                gray
                fullWidth
                sx={{
                    mt: 4,
                }}
            />
            <Field name='role' component='input' type='hidden' />
            <Stack spacing={4} mt={8}>
                <Field
                    name='name'
                    component={InputField}
                    label={t('fields.name')}
                    validate={validators.requiredKey('requiredName')}
                    required
                    inputProps={{
                        maxLength: 100,
                    }}
                />
                <Field
                    name='email'
                    component={InputField}
                    label={t('fields.email', { context: Role[role] })}
                    validate={validators.pipe(validators.requiredKey('requiredEmail'), validators.is.email)}
                    required
                    inputProps={{
                        maxLength: 30,
                    }}
                />
                <Field
                    name='price'
                    component={InputField}
                    label={t('fields.price', { context: Role[role] })}
                    validate={validators.pipe(
                        validators.required,
                        validators.number.min(PRICE_LIMITS[role].min, floatValueHelpers.format),
                        validators.number.max(PRICE_LIMITS[role].max, floatValueHelpers.format),
                    )}
                    required
                    parse={floatValueHelpers.parse}
                    onKeyPress={floatValueHelpers.onKeyPress}
                    format={(value: string, field) => {
                        const fieldState = getFieldState(field as keyof FormValues);
                        return formatCurrency(value, fieldState?.active || !value ? null : '₽');
                    }}
                />
            </Stack>
            <Stack direction='row' gap={2} mt={2} flexWrap='wrap'>
                {basePrices[role]?.map((value, idx) => (
                    <Chip
                        key={idx}
                        variant='primary'
                        label={formatCurrency(value, '₽', 0)}
                        active={Number(price) === value}
                        clickable={Number(price) !== value}
                        onClick={() => change('price', value)}
                    />
                ))}
            </Stack>
        </>
    );
};
